import gsap from "gsap";
import { Container } from "pixi.js";
import {
    SceneShakeConfig,
    SceneShakeConfigType
} from "../../configs/SceneShakeConfig";

let activeShake: gsap.core.Timeline | null = null;

/** Shake strength (0-1) at the given time into the shake */
function getIntensity(time: number, config: SceneShakeConfigType): number {
    const { rampUp, sustain, rampDown, startIntensity } = config;
    const curve = gsap.parseEase(config.intensityEase);

    if (time < rampUp) {
        return startIntensity + (1 - startIntensity) * curve(time / rampUp);
    }
    if (time > rampUp + sustain) {
        return 1 - curve((time - rampUp - sustain) / rampDown);
    }
    return 1;
}

/**
 * Shakes the targets, then restores their original x / y / rotation.
 * Resolves once the shake has finished and the targets are back at rest.
 */
export async function playSceneShake(
    targets: Container[],
    config: SceneShakeConfigType = SceneShakeConfig
): Promise<void> {
    stopSceneShake();

    const { stepDuration, amplitude, ease } = config;
    const total = config.rampUp + config.sustain + config.rampDown;
    const steps = Math.max(1, Math.round(total / stepDuration));
    const rest = targets.map(({ x, y, rotation }) => ({ x, y, rotation }));
    const timeline = gsap.timeline();

    for (let i = 0; i < steps; i++) {
        const intensity = getIntensity((i / steps) * total, config);
        // Alternate the horizontal / rotation direction so it never drifts
        const sign = i % 2 === 0 ? 1 : -1;

        targets.forEach((target, t) => {
            timeline.to(
                target,
                {
                    x: rest[t].x + sign * gsap.utils.random(0.5, 1) * amplitude.x * intensity,
                    y: rest[t].y + gsap.utils.random(-1, 1) * amplitude.y * intensity,
                    rotation: rest[t].rotation + sign * amplitude.rotation * intensity,
                    duration: stepDuration,
                    ease
                },
                i * stepDuration
            );
        });
    }

    // Final tween back to rest (also makes an early stop land on rest)
    targets.forEach((target, t) => {
        timeline.to(target, { ...rest[t], duration: stepDuration }, steps * stepDuration);
    });

    activeShake = timeline;
    await timeline;
    if (activeShake === timeline) {
        activeShake = null;
    }
}

/**
 * Ends a running shake early: the targets snap back to rest and the pending
 * `await playSceneShake(...)` resolves. Does nothing if no shake is running.
 */
export function stopSceneShake(): void {
    activeShake?.progress(1);
}
